// Merge two copies of the Sanskrit progress (the account's copy and this device's copy) without losing either side:
// every lesson keeps its best result, every word and form its most recent state, the last lesson the one opened most
// recently, revision words their latest add or remove, XP the sum of each device's own counter, other counts their
// larger value and the remaining lists their union. Merging the same two copies again gives the same result, so a
// retried sync never double counts.

import type { GameRecord, ItemState, LessonRecord, Progress, RevisionMark } from '@/sanskrit/lib/progress/engine'

import { pruneRevisionLog } from '@/sanskrit/lib/progress/engine'

const later = (a: string | null | undefined, b: string | null | undefined): boolean => (a ?? '') >= (b ?? '')

const mergeMap = <T>(a: Record<string, T>, b: Record<string, T>, pick: (x: T, y: T) => T): Record<string, T> => {
    const out: Record<string, T> = { ...b }

    for (const [key, value] of Object.entries(a)) out[key] = key in b ? pick(value, b[key]) : value

    return out
}

const union = (a: string[], b: string[], cap: number): string[] => Array.from(new Set([...a, ...b])).slice(0, cap)

const lesson = (x: LessonRecord, y: LessonRecord): LessonRecord => {
    const best = x.best >= y.best ? x : y

    return { ...best, best: Math.max(x.best, y.best), attempts: Math.max(x.attempts, y.attempts), passed: x.passed || y.passed }
}

const item = (x: ItemState, y: ItemState): ItemState => (later(x.lastSeen, y.lastSeen) ? x : y)

const game = (x: GameRecord, y: GameRecord): GameRecord => ({
    plays: Math.max(x.plays, y.plays),
    best: Math.max(x.best, y.best),
    level: Math.max(x.level, y.level),
    last: later(x.last, y.last) ? x.last : y.last
})

const total = (counts: Record<string, number>): number => Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0)

/** XP: each device's counter keeps its larger value, plus XP earned before counters existed (the larger of the two). */
const mergeXp = (a: Progress, b: Progress): { xp: number, xpBy: Record<string, number> } => {
    const xpBy = mergeMap(a.xpBy, b.xpBy, (x, y) => Math.max(x, y))
    const untracked = Math.max(a.xp - total(a.xpBy), b.xp - total(b.xpBy), 0)

    return { xp: untracked + total(xpBy), xpBy }
}

const lastLessonTime = (p: Progress): string | null => p.lastLessonAt
    ?? (p.lastLesson ? p.lessons[p.lastLesson]?.completedAt ?? null : null)

const mergeLastLesson = (a: Progress, b: Progress): { lastLesson: string | null, lastLessonAt: string | null } => {
    const pick = !b.lastLesson ? a : !a.lastLesson ? b : later(lastLessonTime(a), lastLessonTime(b)) ? a : b

    return { lastLesson: pick.lastLesson, lastLessonAt: lastLessonTime(pick) }
}

/** Every revision word's state in one copy: its logged add or remove, else "on" with no time for an old copy. */
const marksOf = (p: Progress): Map<string, RevisionMark> => {
    const out = new Map<string, RevisionMark>()

    for (const key of p.revision) out.set(key, { on: true, at: '' })
    for (const [key, mark] of Object.entries(p.revisionLog)) out.set(key, mark)

    return out
}

/** Revision words: the latest add or remove wins, so a word removed on one device stays removed everywhere. */
const mergeRevision = (a: Progress, b: Progress): { revision: string[], revisionLog: Record<string, RevisionMark> } => {
    const ma = marksOf(a)
    const mb = marksOf(b)
    const keys = Array.from(new Set([...ma.keys(), ...mb.keys()]))
    const merged = keys.map(key => {
        const x = ma.get(key)
        const y = mb.get(key)
        const mark = !x ? y as RevisionMark : !y ? x : later(x.at, y.at) ? x : y

        return { key, mark }
    })
    const revision = merged
        .filter(m => m.mark.on)
        .sort((x, y) => (x.mark.at < y.mark.at ? 1 : x.mark.at > y.mark.at ? -1 : 0))
        .map(m => m.key)
        .slice(0, 200)
    const log: Record<string, RevisionMark> = {}

    for (const { key, mark } of merged) if (mark.at) log[key] = mark

    return { revision, revisionLog: pruneRevisionLog(log) }
}

/** `a` wins ties (the copy being kept, usually the account's). */
export const mergeProgress = (a: Progress, b: Progress): Progress => {
    const streak = later(a.streak.last, b.streak.last) ? a.streak : b.streak
    const history = [...a.history, ...b.history]
        .filter((h, i, all) => all.findIndex(o => o.at === h.at && o.item === h.item) === i)
        .sort((x, y) => (x.at < y.at ? -1 : x.at > y.at ? 1 : 0))
        .slice(-300)

    return {
        version: 1,
        ...mergeXp(a, b),
        streak: { ...streak, best: Math.max(a.streak.best, b.streak.best, streak.count) },
        daily: mergeMap(a.daily, b.daily, (x, y) => Math.max(x, y)),
        lessons: mergeMap(a.lessons, b.lessons, lesson),
        ...mergeLastLesson(a, b),
        items: mergeMap(a.items, b.items, item),
        skills: mergeMap(
            a.skills as Record<string, { correct: number, total: number, last: string | null }>,
            b.skills as Record<string, { correct: number, total: number, last: string | null }>,
            (x, y) => (x.total >= y.total ? x : y)
        ) as Progress['skills'],
        confusions: mergeMap(a.confusions, b.confusions, (x, y) => Math.max(x, y)),
        recentWords: union(a.recentWords, b.recentWords, 30),
        ...mergeRevision(a, b),
        games: mergeMap(a.games, b.games, game),
        achievements: union(a.achievements, b.achievements, 500),
        history,
        storiesRead: union(a.storiesRead, b.storiesRead, 1000)
    }
}

/** True when a copy holds anything worth keeping. */
export const hasProgress = (p: Progress): boolean => p.xp > 0
    || Object.keys(p.lessons).length > 0
    || Object.keys(p.items).length > 0
    || Object.keys(p.games).length > 0
    || p.storiesRead.length > 0
    || p.revision.length > 0
    || Object.keys(p.revisionLog).length > 0
